import authService from './authService';
import {
  SefazCommunicationLog,
  SefazCommunicationLogListFilters,
  SefazCommunicationLogListResult,
} from '../types/sefazCommunicationLog';
import { ApiResponse } from '../types/common';

/**
 * Service para Log de Comunicação SEFAZ
 *
 * Endpoints (base /api/v1):
 *   GET /sefaz-communication-logs
 *   GET /sefaz-communication-logs/:id
 *   POST /sefaz-communication-logs/export-xml  (export response XMLs as ZIP)
 *
 * Restrito ao papel `admin` (permissão sefaz_communication_log.read).
 */

interface PaginationPayload {
  page?: number;
  page_size?: number;
  pageSize?: number;
  total?: number;
  total_items?: number;
  total_pages?: number;
  totalPages?: number;
}

const normalizePagination = (
  pg: PaginationPayload | undefined,
  filters: SefazCommunicationLogListFilters,
  itemsLength: number,
) => ({
  page: pg?.page ?? filters.page ?? 1,
  pageSize: pg?.page_size ?? pg?.pageSize ?? filters.pageSize ?? 20,
  total: pg?.total_items ?? pg?.total ?? itemsLength,
  totalPages: pg?.total_pages ?? pg?.totalPages ?? 1,
});

class SefazCommunicationLogService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private buildListParams(filters: SefazCommunicationLogListFilters): string {
    const params = new URLSearchParams();
    params.set('page', String(filters.page ?? 1));
    params.set('page_size', String(filters.pageSize ?? 20));
    params.set('company_id', String(filters.company_id));
    if (filters.service_type) params.set('service_type', filters.service_type);
    if (filters.invoice_number !== undefined) params.set('invoice_number', String(filters.invoice_number));
    if (filters.serie !== undefined) params.set('serie', String(filters.serie));
    if (filters.access_key) params.set('access_key', filters.access_key);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    return params.toString();
  }

  async list(filters: SefazCommunicationLogListFilters): Promise<SefazCommunicationLogListResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/sefaz-communication-logs?${this.buildListParams(filters)}`);
    if (!response.ok) {
      if (response.status === 403) throw new Error('Acesso restrito a administradores');
      throw new Error('Falha ao carregar logs SEFAZ');
    }
    const json = await response.json();
    const items: SefazCommunicationLog[] = Array.isArray(json.data) ? json.data : [];
    return {
      data: items,
      pagination: normalizePagination(json.pagination, filters, items.length),
    };
  }

  async getById(id: number): Promise<SefazCommunicationLog> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/sefaz-communication-logs/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Log SEFAZ não encontrado');
      if (response.status === 403) throw new Error('Acesso restrito a administradores');
      throw new Error('Falha ao carregar log SEFAZ');
    }
    const data: ApiResponse<SefazCommunicationLog> = await response.json();
    return data.data!;
  }

  /**
   * Export response XMLs for selected SEFAZ communication logs as ZIP
   * POST /sefaz-communication-logs/export-xml
   * Body: { sefaz_log_ids: number[] }
   * Response: application/zip blob
   */
  async exportResponseXmlZip(sefazLogIds: number[]): Promise<void> {
    const { getConfig } = await import('../config');
    const baseUrl = getConfig().api.baseUrl;

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/sefaz-communication-logs/export-xml`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ sefaz_log_ids: sefazLogIds }),
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error((body as Record<string, string>).message || `Erro ao exportar XMLs (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `sefaz-response-xmls.zip`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export const sefazCommunicationLogService = new SefazCommunicationLogService();
export default sefazCommunicationLogService;
